import React, { useEffect } from 'react';
import styles from './index.module.css';
import {
  Form,
  Button,
  FormInstance,
  Modal,
  Switch,
  Select,
  message
} from 'antd';
import * as translations from './strings';
import { getLangKey } from '../../../../javascript/models/Language';
import { Project } from '../project_settings/forms/general/types';
import cx from 'classnames';
import { get } from '../../utils/fetch';
import HelpArticleButton from 'appJS/utils/HelpArticleButton';
import { locales } from './locales';
import { CornerDownRight } from 'lucide-react';

const strings = translations[getLangKey()];

type Props = {
  form: FormInstance<any>;
  submit: () => void;
  forceUpdate: () => void;
  project?: Project;
  organization?: {
    id: number;
    ai_suggested_tags: boolean;
    ai_suggested_titles: boolean;
    ai_duplicate_tasks: boolean;
    ai_suggested_tags_existing_only?: boolean;
    ai_suggested_titles_default_language?: string;
  };
  loading: boolean;
  onOptInOrOut: (optIn: boolean) => void;
  container: HTMLDivElement;
  duplicateTasksExperiment: boolean;
};

enum FieldName {
  AI_SUGGESTED_TAGS = 'aiSuggestedTags',
  AI_SUGGESTED_TITLES = 'aiSuggestedTitles',
  AI_DUPLICATE_TASKS = 'aiDuplicateTasks',
  AI_SUGGESTED_TAGS_EXISTING_ONLY = 'aiSuggestedTagsExistingOnly',
  AI_SUGGESTED_TITLES_DEFAULT_LANGUAGE = 'aiSuggestedTitlesDefaultLanguage'
}

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

const camelToSnakeCase = (str: string) =>
  str.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`).toLowerCase();

export const handleEnableOrDisableBeta = async ({
  callback,
  organizationId,
  enable
}: {
  callback: (optIn: boolean) => void;
  organizationId?: number | string;
  enable?: boolean;
}) => {
  if (!organizationId) return;
  try {
    const response = await get(
      `/settings/organizations/${organizationId}/experiments/enable?experiment_name=ai_features`
    );
    if (response.ok) {
      message.success(strings[`betaNow${enable ? 'Enabled' : 'Disabled'}`]);
      callback(!!enable);
    } else {
      message.error(response.error);
    }
  } catch (error) {
    console.error(error);
    message.error('Something went wrong, please try again later.');
  }
};

export const fieldNames = (duplicateTasksExperiment: boolean) =>
  Array.from(Object.values(FieldName)).filter(
    fieldName =>
      fieldName !== FieldName.AI_DUPLICATE_TASKS || duplicateTasksExperiment
  );

export const AISettingsForm = ({
  form,
  submit,
  forceUpdate,
  project,
  organization,
  loading,
  onOptInOrOut,
  container,
  duplicateTasksExperiment
}: Props) => {
  const organizationId = project?.organizationId || organization?.id;
  const fieldNamesForForm = fieldNames(duplicateTasksExperiment);
  const disabledStatus = fieldNamesForForm.reduce((acc, fieldName) => {
    const status = !!project?.[`org${capitalize(fieldName)}`];
    switch (fieldName) {
      case FieldName.AI_SUGGESTED_TAGS_EXISTING_ONLY:
      case FieldName.AI_SUGGESTED_TITLES_DEFAULT_LANGUAGE:
        acc[fieldName] = status;
        break;
      default:
        acc[fieldName] = !status;
    }
    return acc;
  }, {});

  const showConfirm = () => {
    Modal.confirm({
      title: strings.confirmDisableBetaTitle,
      content: strings.confirmDisableBetaContent,
      okText: strings.cancel,
      cancelText: strings.disableBeta,
      onOk: close => close(),
      okButtonProps: {
        type: 'primary',
        size: 'middle'
      },
      wrapClassName: styles.disableBetaModal,
      autoFocusButton: null,
      centered: true,
      cancelButtonProps: {
        type: 'default',
        className: styles.disableBeta,
        size: 'middle',
        shape: 'round'
      },
      onCancel: close => {
        handleEnableOrDisableBeta({
          callback: () => onOptInOrOut(false),
          organizationId
        });
        close();
      },
      maskClosable: true,
      getContainer: () => container
    });
  };

  const initialValues = fieldNamesForForm.reduce((acc, fieldName) => {
    if (project) {
      if (
        fieldName === FieldName.AI_SUGGESTED_TAGS_EXISTING_ONLY ||
        fieldName === FieldName.AI_SUGGESTED_TITLES_DEFAULT_LANGUAGE
      ) {
        acc[fieldName] =
          project[`org${capitalize(fieldName)}`] || project[fieldName];
      } else {
        acc[fieldName] =
          project[`org${capitalize(fieldName)}`] && project[fieldName];
      }
    } else if (organization) {
      acc[fieldName] = organization?.[camelToSnakeCase(fieldName)];
    }
    return acc;
  }, {});

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [project, form]);

  const formAiTags = form.getFieldValue(FieldName.AI_SUGGESTED_TAGS);
  const showExistingOnly =
    typeof formAiTags === 'boolean'
      ? !!formAiTags
      : !!initialValues[FieldName.AI_SUGGESTED_TAGS];
  const formAiTitles = form.getFieldValue(FieldName.AI_SUGGESTED_TITLES);
  const showDefaultLanguage =
    typeof formAiTitles === 'boolean'
      ? !!formAiTitles
      : !!initialValues[FieldName.AI_SUGGESTED_TITLES];
  const uiFieldNames: FieldName[] = [
    FieldName.AI_SUGGESTED_TAGS,
    ...(showExistingOnly ? [FieldName.AI_SUGGESTED_TAGS_EXISTING_ONLY] : []),
    FieldName.AI_SUGGESTED_TITLES,
    ...(showDefaultLanguage
      ? [FieldName.AI_SUGGESTED_TITLES_DEFAULT_LANGUAGE]
      : []),
    ...(duplicateTasksExperiment ? [FieldName.AI_DUPLICATE_TASKS] : [])
  ];

  const featureArticlesIds = {
    aiSuggestedTags: 12575984,
    aiSuggestedTitles: 12574826,
    aiDuplicateTasks: 12652307,
    aiSuggestedTagsExistingOnly: 0,
    aiSuggestedTitlesDefaultLanguage: 0
  };

  const options: { label: string; value: string }[] = [
    { label: strings.automaticallyDetectLanguage, value: '' },
    ...locales.map(({ label, flag, locale }) => ({
      label: flag ? `${flag} ${label} (${locale})` : label,
      value: locale
    }))
  ];

  return (
    <Form
      form={form}
      onFinish={submit}
      initialValues={initialValues}
      layout="vertical"
      className={styles.form}
      onValuesChange={() => forceUpdate()}
    >
      {uiFieldNames.map(name => {
        const isDisabled = project && disabledStatus[name];
        const isLanguageField =
          name === FieldName.AI_SUGGESTED_TITLES_DEFAULT_LANGUAGE;
        const isNestedField =
          name === FieldName.AI_SUGGESTED_TAGS_EXISTING_ONLY || isLanguageField;
        const labelText = strings[`${name}Label`];
        return (
          <Form.Item
            key={name}
            className={cx(styles.formItem, {
              [styles.disabled]: isDisabled,
              [styles.nested]: isNestedField,
              [styles.languageField]: isLanguageField
            })}
            label={
              <span
                className={cx(styles.switchLabel, {
                  [styles.disabledLabel]: isDisabled
                })}
              >
                <span className={styles.switchLabelContent}>
                  <span className={styles.switchLabelText}>
                    {labelText}
                    {isDisabled &&
                      (isNestedField ? (
                        <span className={styles.disabledText}>
                          ({strings.enabledForAllProjects})
                        </span>
                      ) : (
                        <span className={styles.disabledText}>
                          ({strings.disabledForAllProjects})
                        </span>
                      ))}
                  </span>
                </span>
                {featureArticlesIds[name] ? (
                  <span className={styles.switchLabelHelp}>
                    <HelpArticleButton articleId={featureArticlesIds[name]} />
                  </span>
                ) : null}
              </span>
            }
          >
            <div className={styles.nestedBranchIconContainer}>
              {isNestedField ? (
                <div>
                  <CornerDownRight
                    className={styles.nestedBranchIcon}
                    size={16}
                  />
                </div>
              ) : null}
              <Form.Item
                noStyle
                name={name}
                valuePropName={isLanguageField ? 'value' : 'checked'}
              >
                {isLanguageField ? (
                  <Select
                    showSearch
                    placeholder={strings.selectLanguagePlaceholder}
                    disabled={isDisabled}
                    className={styles.localeSelect}
                    optionFilterProp="children"
                    popupClassName={styles.localeSelectPopup}
                    getPopupContainer={() => container}
                    filterOption={(inputValue, option) =>
                      !!locales.filter(({ label, locale, language }) => {
                        const search = inputValue.toLowerCase();
                        return (
                          label.toLowerCase().includes(search) ||
                          locale.toLowerCase().includes(search) ||
                          language.toLowerCase().includes(search)
                        );
                      })
                    }
                    options={options}
                    notFoundContent={strings.noResultsFound}
                  />
                ) : (
                  <Switch disabled={isDisabled} />
                )}
              </Form.Item>
            </div>
          </Form.Item>
        );
      })}
      <div className={styles.buttonsContainer}>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={loading}
          className={styles.saveButton}
        >
          {strings.save}
        </Button>
        <Button
          type="default"
          onClick={showConfirm}
          className={styles.disableBeta}
        >
          {strings.disableBeta}
        </Button>
      </div>
    </Form>
  );
};
